/**
 * GPU-backed paint target for `NativeVideoReceiver`. Wraps an
 * `HTMLCanvasElement` with a WebGL context, a textured-quad shader,
 * and a single `TEXTURE_2D` that the receiver uploads each decoded
 * `VideoFrame` into via `gl.texImage2D`.
 *
 * Why not 2D canvas: `ctx.drawImage(videoFrame, ...)` on the receiver's
 * platforms takes wildly different fast paths. Chromium (Win WebView2
 * and most browsers) has a zero-copy GPU path; WebKitGTK (Linux native)
 * tends to fall back to Cairo CPU compositing and burns power on every
 * paint; WKWebView (macOS native) sits in between with an IOSurface
 * readback. WebGL `texImage2D(videoFrame)` is the most consistent path
 * across all three — it forces the upload through the GL stack which
 * is GPU-accelerated everywhere we care about.
 *
 * Multi-mount: each visible stream tile owns one of these. The decoder
 * still produces a fresh `VideoFrame` per source frame, but multi-tile
 * scenarios (grid + fullscreen overlay) only multiply the *upload*
 * cost N times, not the CPU-composite cost. On WebKitGTK that's the
 * difference between burning a core and being roughly free.
 */
export class WebGLVideoTarget {
  private readonly gl: WebGLRenderingContext;
  private readonly program: WebGLProgram;
  private readonly positionBuffer: WebGLBuffer;
  private readonly uvBuffer: WebGLBuffer;
  private readonly texture: WebGLTexture;
  private readonly attribPos: number;
  private readonly attribUv: number;
  /// Last visibleRect we wrote into the UV buffer. The buffer only
  /// needs reuploading when the source's visible region changes
  /// (rare — basically never mid-stream for our content).
  private cachedUv: {
    x: number;
    y: number;
    w: number;
    h: number;
    codedW: number;
    codedH: number;
  } | null = null;
  private disposed = false;

  /**
   * Construct a paint target on `canvas`. Throws if WebGL isn't
   * available — caller is expected to fall back to a 2D context in
   * that case (which has to happen *before* WebGL was attempted on
   * the same canvas, since `getContext` is one-shot per context type
   * family).
   */
  constructor(public readonly canvas: HTMLCanvasElement) {
    // - `alpha: false`: video is opaque, skip the alpha channel and let
    //   the browser composite us as opaque
    // - `premultipliedAlpha: true` (default): irrelevant for opaque content
    // - `antialias: false`: 1:1 textured blit, MSAA is wasted GPU time
    // - `preserveDrawingBuffer: false` (default): we redraw every
    //   frame, no need to keep an extra back-buffer around
    // - `powerPreference: 'low-power'`: nudges hybrid-GPU systems
    //   (laptops) to use the integrated GPU instead of spinning up
    //   the discrete one for a simple video blit
    const gl = canvas.getContext('webgl', {
      alpha: false,
      antialias: false,
      powerPreference: 'low-power',
    });
    if (!gl) throw new Error('WebGL context unavailable');
    this.gl = gl;

    const vs = compileShader(
      gl,
      gl.VERTEX_SHADER,
      `attribute vec2 a_pos;
       attribute vec2 a_uv;
       varying vec2 v_uv;
       void main() {
         v_uv = a_uv;
         gl_Position = vec4(a_pos, 0.0, 1.0);
       }`,
    );
    const fs = compileShader(
      gl,
      gl.FRAGMENT_SHADER,
      `precision mediump float;
       uniform sampler2D u_tex;
       varying vec2 v_uv;
       void main() {
         gl_FragColor = texture2D(u_tex, v_uv);
       }`,
    );
    this.program = linkProgram(gl, vs, fs);
    gl.deleteShader(vs);
    gl.deleteShader(fs);

    this.attribPos = gl.getAttribLocation(this.program, 'a_pos');
    this.attribUv = gl.getAttribLocation(this.program, 'a_uv');

    // Triangle-strip quad covering the full clip space [-1, 1]^2.
    // Order: BL, BR, TL, TR — pairs with the UVs below.
    this.positionBuffer = mustCreateBuffer(gl);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);

    // UV buffer is rebuilt lazily from `visibleRect` in `paint()` —
    // initial contents don't matter but the buffer needs to exist.
    this.uvBuffer = mustCreateBuffer(gl);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(8), gl.DYNAMIC_DRAW);

    this.texture = mustCreateTexture(gl);
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    // Linear filter so downscaled tiles don't show block edges;
    // CLAMP_TO_EDGE so the very last column / row doesn't sample
    // wrapped from the opposite side when the UVs land exactly on
    // pixel boundaries.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    // No `UNPACK_FLIP_Y_WEBGL`. The UV buffer below pairs screen-top
    // with v=0 and screen-bottom with v=1 — that already matches the
    // "row 0 of VideoFrame is the top of the image, uploaded to v=0"
    // GL default. Turning on FLIP_Y on top of that double-flips and
    // produces an inverted image.
  }

  /**
   * Upload `frame` and draw it. Idempotent re: GL state — we re-bind
   * everything per call so a target sharing context with anything
   * else (it shouldn't, since each target owns its own canvas) would
   * still produce correct output. Throws on GL errors; the receiver
   * catches and falls back / logs.
   */
  paint(frame: VideoFrame): void {
    if (this.disposed) return;
    const gl = this.gl;

    // displayWidth / displayHeight is the intended render rect (sans
    // H264 block padding). Match the canvas to that so CSS sizing
    // doesn't distort the aspect ratio.
    const dw = frame.displayWidth || frame.codedWidth;
    const dh = frame.displayHeight || frame.codedHeight;
    if (this.canvas.width !== dw) this.canvas.width = dw;
    if (this.canvas.height !== dh) this.canvas.height = dh;
    gl.viewport(0, 0, dw, dh);

    // Rebuild UVs when the source's visible region changes. For a
    // typical stream this fires exactly once (on the first frame).
    const vr = frame.visibleRect;
    const cw = frame.codedWidth;
    const ch = frame.codedHeight;
    const vx = vr ? vr.x : 0;
    const vy = vr ? vr.y : 0;
    const vw = vr ? vr.width : cw;
    const vh = vr ? vr.height : ch;
    const cur = this.cachedUv;
    if (
      !cur ||
      cur.x !== vx ||
      cur.y !== vy ||
      cur.w !== vw ||
      cur.h !== vh ||
      cur.codedW !== cw ||
      cur.codedH !== ch
    ) {
      const u0 = vx / cw;
      const v0 = vy / ch;
      const u1 = (vx + vw) / cw;
      const v1 = (vy + vh) / ch;
      // Match position order BL, BR, TL, TR.
      gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
      gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array([u0, v1, u1, v1, u0, v0, u1, v0]),
        gl.DYNAMIC_DRAW,
      );
      this.cachedUv = { x: vx, y: vy, w: vw, h: vh, codedW: cw, codedH: ch };
    }

    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    // VideoFrame is a `TexImageSource` per the WebCodecs spec — the
    // browser internally picks the cheapest transfer path (zero-copy
    // texture import when the frame is GPU-backed, else upload).
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, frame);

    gl.useProgram(this.program);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    gl.enableVertexAttribArray(this.attribPos);
    gl.vertexAttribPointer(this.attribPos, 2, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
    gl.enableVertexAttribArray(this.attribUv);
    gl.vertexAttribPointer(this.attribUv, 2, gl.FLOAT, false, 0, 0);

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    const gl = this.gl;
    gl.deleteTexture(this.texture);
    gl.deleteBuffer(this.positionBuffer);
    gl.deleteBuffer(this.uvBuffer);
    gl.deleteProgram(this.program);
    // Force GPU resources to release immediately rather than waiting
    // for the canvas GC — important when N tiles are constantly
    // mounting / unmounting (e.g. peer joins / leaves).
    const lose = gl.getExtension('WEBGL_lose_context');
    lose?.loseContext();
  }
}

function compileShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('createShader returned null');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`shader compile failed: ${log}`);
  }
  return shader;
}

function linkProgram(gl: WebGLRenderingContext, vs: WebGLShader, fs: WebGLShader): WebGLProgram {
  const program = gl.createProgram();
  if (!program) throw new Error('createProgram returned null');
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`program link failed: ${log}`);
  }
  return program;
}

function mustCreateBuffer(gl: WebGLRenderingContext): WebGLBuffer {
  const buf = gl.createBuffer();
  if (!buf) throw new Error('createBuffer returned null');
  return buf;
}

function mustCreateTexture(gl: WebGLRenderingContext): WebGLTexture {
  const tex = gl.createTexture();
  if (!tex) throw new Error('createTexture returned null');
  return tex;
}
